import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Phone, Calendar, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LeadForm } from '@/components/forms/LeadForm';
import { useSiteContent } from '@/context/SiteContentContext';
import { cn } from '@/lib/utils';

type WidgetView = 'menu' | 'form';

export const FloatingFormWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<WidgetView>('menu');
  const { template } = useSiteContent();

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => setView('menu'), 300);
  };

  const handleFormSuccess = () => {
    setTimeout(handleClose, 2000);
  };

  return (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setIsOpen(true)}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:shadow-xl transition-shadow"
            aria-label="Open contact menu"
          >
            <MessageCircle className="h-6 w-6" />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Widget Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleClose}
              className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:bg-transparent md:backdrop-blur-none"
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className={cn(
                'fixed z-50 bg-card border border-border rounded-2xl shadow-2xl overflow-hidden',
                'bottom-6 right-6 w-[calc(100vw-3rem)] max-w-sm',
                'md:bottom-6 md:right-6 md:w-96'
              )}
            >
              {/* Header */}
              <div className="bg-primary text-primary-foreground p-4 flex items-center justify-between">
                <h3 className="font-heading font-semibold">
                  {view === 'menu' ? 'How can we help?' : 'Request Free Quote'}
                </h3>
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-primary-foreground/20 rounded-full transition-colors"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 max-h-[70vh] overflow-y-auto">
                <AnimatePresence mode="wait">
                  {view === 'menu' ? (
                    <motion.div
                      key="menu"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="space-y-3"
                    >
                      {/* Quick Actions */}
                      <button
                        onClick={() => setView('form')}
                        className="w-full flex items-center gap-4 p-4 bg-muted hover:bg-muted/80 rounded-xl transition-colors text-left group"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <FileText className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Get Free Estimate</p>
                          <p className="text-sm text-muted-foreground">We'll respond within 24hrs</p>
                        </div>
                      </button>

                      <a
                        href={`tel:${template.contact?.phone?.replace(/\D/g, '') || ''}`}
                        className="w-full flex items-center gap-4 p-4 bg-muted hover:bg-muted/80 rounded-xl transition-colors text-left group"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Phone className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Call Now</p>
                          <p className="text-sm text-muted-foreground">{template.contact?.phone || 'Available 24/7'}</p>
                        </div>
                      </a>

                      <a
                        href="/contact"
                        className="w-full flex items-center gap-4 p-4 bg-muted hover:bg-muted/80 rounded-xl transition-colors text-left group"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          <Calendar className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">Schedule Service</p>
                          <p className="text-sm text-muted-foreground">Book an appointment online</p>
                        </div>
                      </a>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="form"
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                    >
                      <button
                        onClick={() => setView('menu')}
                        className="text-sm text-muted-foreground hover:text-foreground mb-4 flex items-center gap-1"
                      >
                        ← Back to menu
                      </button>
                      <LeadForm
                        title=""
                        subtitle=""
                        showPromoField={true}
                        source="floating_widget"
                        onSuccess={handleFormSuccess}
                      />
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
